import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Plus, PhoneCall, Hospital, MapPin } from "@phosphor-icons/react";

export default function DeliveryPrep() {
  const [template, setTemplate] = useState([]);
  const [bag, setBag] = useState({ checked: {}, custom: [] });
  const [contacts, setContacts] = useState([]);
  const [plan, setPlan] = useState({});
  const [newContact, setNewContact] = useState({ name: "", relation: "", phone: "", is_primary: false });
  const [customItem, setCustomItem] = useState("");

  const load = async () => {
    const [t, b, c, p] = await Promise.all([
      api.get("/prep/hospital-bag/template"),
      api.get("/prep/hospital-bag"),
      api.get("/prep/emergency-contacts"),
      api.get("/prep/birth-plan").catch(() => ({ data: {} })),
    ]);
    setTemplate(t.data);
    setBag(b.data);
    setContacts(c.data);
    setPlan(p.data || {});
  };
  useEffect(() => { load(); }, []);

  const toggle = async (item_id) => {
    const newCheck = !bag.checked?.[item_id];
    setBag({ ...bag, checked: { ...bag.checked, [item_id]: newCheck } });
    await api.post("/prep/hospital-bag/toggle", { item_id, checked: newCheck });
  };

  const addCustomItem = async () => {
    if (!customItem.trim()) return;
    const r = await api.post("/prep/hospital-bag/custom", { label: customItem, category: "custom" });
    setBag({ ...bag, custom: [...(bag.custom || []), r.data] });
    setCustomItem("");
  };

  const savePlan = async () => {
    try { await api.post("/prep/birth-plan", plan); toast.success("Birth plan saved."); }
    catch { toast.error("Could not save."); }
  };

  const addContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prep/emergency-contacts", newContact);
      setNewContact({ name: "", relation: "", phone: "", is_primary: false });
      const c = await api.get("/prep/emergency-contacts");
      setContacts(c.data);
    } catch { toast.error("Could not add."); }
  };

  const delContact = async (id) => { await api.delete(`/prep/emergency-contacts/${id}`); const c = await api.get("/prep/emergency-contacts"); setContacts(c.data); };

  const allItems = [...template, ...(bag.custom || [])];
  const checkedCount = allItems.filter((i) => bag.checked?.[i.id]).length;
  const pct = allItems.length ? Math.round((checkedCount / allItems.length) * 100) : 0;
  const categories = ["documents", "mother", "baby", "partner", "custom"];

  return (
    <div className="space-y-10" data-testid="delivery-prep-page">
      <header>
        <p className="overline text-saffron mb-2">Delivery readiness</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Hospital readiness center</h1>
        <p className="text-ink-700 mt-2">Everything ready. So when the moment comes, you can simply breathe.</p>
      </header>

      <div className="vansha-card p-8" data-testid="readiness-meter">
        <p className="overline text-saffron mb-2">Readiness</p>
        <div className="flex items-baseline gap-3">
          <span className="font-serif text-6xl text-terracotta">{pct}%</span>
          <span className="text-ink-500">{checkedCount}/{allItems.length} packed</span>
        </div>
        <div className="mt-4 h-2 bg-vansha-accent rounded-full overflow-hidden">
          <div className="h-full bg-terracotta transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <section data-testid="hospital-bag-section">
        <h2 className="font-serif text-3xl mb-6">Hospital bag</h2>
        {categories.map((cat) => {
          const itemsInCat = allItems.filter((i) => i.category === cat);
          if (itemsInCat.length === 0) return null;
          return (
            <div key={cat} className="mb-6">
              <p className="overline text-saffron mb-3 capitalize">{cat}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {itemsInCat.map((it) => {
                  const isChecked = !!bag.checked?.[it.id];
                  return (
                    <button key={it.id} onClick={() => toggle(it.id)} data-testid={`bag-${it.id}`}
                      className={`vansha-card p-4 flex items-center gap-3 text-left transition ${isChecked ? "opacity-60" : ""}`}>
                      <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isChecked ? "bg-terracotta border-terracotta" : "border-ink-20"}`}>
                        {isChecked && <Check weight="bold" size={14} className="text-white" />}
                      </div>
                      <span className={`text-sm ${isChecked ? "line-through text-ink-500" : "text-ink-900"}`}>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="vansha-card p-4 mt-4 flex gap-2" data-testid="add-custom-bag">
          <input value={customItem} onChange={(e) => setCustomItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomItem())} className="input-line flex-1" placeholder="Add something specific…" data-testid="custom-bag-input" />
          <button onClick={addCustomItem} className="btn-secondary" data-testid="custom-bag-btn"><Plus weight="thin" size={16} /> Add</button>
        </div>
      </section>

      <section data-testid="birth-plan-section">
        <h2 className="font-serif text-3xl mb-6">Birth plan</h2>
        <div className="vansha-card p-6 grid sm:grid-cols-2 gap-5">
          <Field label="Hospital name" value={plan.hospital_name} onChange={(v) => setPlan({ ...plan, hospital_name: v })} tid="bp-hosp-name" />
          <Field label="Hospital phone" value={plan.hospital_phone} onChange={(v) => setPlan({ ...plan, hospital_phone: v })} tid="bp-hosp-phone" />
          <Field label="Primary doctor" value={plan.primary_doctor} onChange={(v) => setPlan({ ...plan, primary_doctor: v })} tid="bp-doctor" />
          <Field label="Doctor phone" value={plan.doctor_phone} onChange={(v) => setPlan({ ...plan, doctor_phone: v })} tid="bp-doctor-phone" />
          <div className="sm:col-span-2"><label className="overline mb-2 block">Preferred delivery</label>
            <select value={plan.preferred_delivery || "open"} onChange={(e) => setPlan({ ...plan, preferred_delivery: e.target.value })} className="input-line bg-transparent" data-testid="bp-delivery">
              <option value="open">Open to medical recommendation</option><option value="normal">Normal (vaginal)</option><option value="csection">C-section</option>
            </select>
          </div>
          <Field label="Pain management preference" value={plan.pain_management} onChange={(v) => setPlan({ ...plan, pain_management: v })} tid="bp-pain" />
          <Field label="Transport plan" value={plan.transport_plan} onChange={(v) => setPlan({ ...plan, transport_plan: v })} tid="bp-transport" />
          <div className="sm:col-span-2"><label className="overline mb-2 block">Special requests</label><textarea value={plan.special_requests || ""} onChange={(e) => setPlan({ ...plan, special_requests: e.target.value })} className="input-line" rows="3" data-testid="bp-special" /></div>
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={savePlan} className="btn-primary" data-testid="bp-save">Save birth plan</button>
          </div>
        </div>
      </section>

      <section data-testid="contacts-section">
        <h2 className="font-serif text-3xl mb-6">Emergency contacts</h2>
        <form onSubmit={addContact} className="vansha-card p-6 grid sm:grid-cols-4 gap-4 items-end mb-4">
          <div><label className="overline mb-2 block">Name</label><input required value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className="input-line" data-testid="ec-name" /></div>
          <div><label className="overline mb-2 block">Relation</label><input value={newContact.relation} onChange={(e) => setNewContact({ ...newContact, relation: e.target.value })} className="input-line" data-testid="ec-relation" /></div>
          <div><label className="overline mb-2 block">Phone</label><input required value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="input-line" data-testid="ec-phone" /></div>
          <button className="btn-primary" data-testid="ec-submit"><Plus weight="thin" size={16} /> Add</button>
        </form>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((c) => (
            <div key={c.id} className="vansha-card p-5" data-testid={`contact-${c.id}`}>
              <PhoneCall weight="thin" size={22} className="text-terracotta mb-2" />
              <h3 className="font-serif text-xl">{c.name}</h3>
              {c.relation && <p className="text-sm text-ink-500">{c.relation}</p>}
              <a href={`tel:${c.phone}`} className="text-saffron font-medium mt-1 block" data-testid={`call-${c.id}`}>{c.phone}</a>
              <button onClick={() => delContact(c.id)} className="text-xs text-ink-500 mt-2 hover:text-terracotta">Remove</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, tid }) {
  return <div><label className="overline mb-2 block">{label}</label><input value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-line" data-testid={tid} /></div>;
}
